import React, { useState, useEffect } from 'react';
import {
  StatisticsManager,
  DetailedStatistics,
  LeaderboardEntry,
  StatisticsBreakdown,
  Achievement
} from '../services/StatisticsManager';
import { ProfileManager } from '../services/ProfileManager';
import { GameMode, AIDifficulty } from '../types';
import './StatisticsDisplay.css';

interface StatisticsDisplayProps {
  statisticsManager: StatisticsManager;
  profileManager: ProfileManager;
}

type ViewMode = 'personal' | 'leaderboard' | 'achievements';

export const StatisticsDisplay: React.FC<StatisticsDisplayProps> = ({
  statisticsManager,
  profileManager
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('personal');
  const [detailedStats, setDetailedStats] = useState<DetailedStatistics | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [selectedGameMode, setSelectedGameMode] = useState<GameMode | undefined>();
  const [selectedDifficulty, setSelectedDifficulty] = useState<AIDifficulty | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPersonalStatistics();
  }, []);

  useEffect(() => {
    if (viewMode === 'leaderboard') {
      loadLeaderboard();
    }
  }, [viewMode, selectedGameMode, selectedDifficulty]);

  const loadPersonalStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const stats = await statisticsManager.getPersonalStatistics();
      setDetailedStats(stats);
    } catch (err) {
      setError('Failed to load personal statistics');
      console.error('Error loading personal statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const leaderboardData = await statisticsManager.getGlobalLeaderboard(
        selectedGameMode,
        selectedDifficulty
      );
      setLeaderboard(leaderboardData);
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error('Error loading leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetStatistics = async () => {
    if (window.confirm('Are you sure you want to reset all statistics? This action cannot be undone.')) {
      try {
        await statisticsManager.resetStatistics();
        await loadPersonalStatistics();
      } catch (err) {
        setError('Failed to reset statistics');
        console.error('Error resetting statistics:', err);
      }
    }
  };

  const handleExportStatistics = () => {
    try {
      const exportData = statisticsManager.exportStatistics();
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tic-tac-toe-stats-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export statistics');
      console.error('Error exporting statistics:', err);
    }
  };

  const renderPersonalStatistics = () => {
    if (!detailedStats) return null;

    return (
      <div className="personal-statistics">
        <div className="stats-overview">
          <div className="stat-card">
            <h3>Total Games</h3>
            <div className="stat-value">{detailedStats.personal.totalGames}</div>
          </div>
          <div className="stat-card">
            <h3>Win Rate</h3>
            <div className="stat-value">{(detailedStats.personal.winRate * 100).toFixed(1)}%</div>
          </div>
        </div>

        <div className="stats-breakdown">
          <h3>Statistics by Category</h3>
          {detailedStats.breakdown.map((category, index) => (
            <div key={index} className="category-stats">
              <h4>{category.category}</h4>
              <div className="category-details">
                <span className="wins">Wins: {category.wins}</span>
                <span className="losses">Losses: {category.losses}</span>
                <span className="draws">Draws: {category.draws}</span>
                <span className="win-rate">Win Rate: {(category.winRate * 100).toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="recent-games">
          <h3>Recent Games</h3>
          {detailedStats.recentGames.length > 0 ? (
            <div className="games-list">
              {detailedStats.recentGames.slice(0, 5).map((game, index) => (
                <div key={index} className="game-result">
                  <span className="game-mode">{game.mode}</span>
                  {game.difficulty && <span className="difficulty">{game.difficulty}</span>}
                  <span className={`result ${game.winner === 'player' ? 'win' : game.isDraw ? 'draw' : 'loss'}`}>
                    {game.winner === 'player' ? 'Win' : game.isDraw ? 'Draw' : 'Loss'}
                  </span>
                  <span className="date">{new Date(game.completedAt).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-games">No recent games</p>
          )}
        </div>

        <div className="stats-actions">
          <button onClick={handleExportStatistics} className="export-btn">
            Export Statistics
          </button>
          <button onClick={handleResetStatistics} className="reset-btn">
            Reset Statistics
          </button>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => {
    return (
      <div className="leaderboard">
        <div className="leaderboard-filters">
          <select
            value={selectedGameMode || ''}
            onChange={(e) => setSelectedGameMode(e.target.value as GameMode || undefined)}
          >
            <option value="">All Game Modes</option>
            <option value={GameMode.SINGLE_PLAYER}>Single Player</option>
            <option value={GameMode.MULTIPLAYER}>Multiplayer</option>
          </select>

          {selectedGameMode === GameMode.SINGLE_PLAYER && (
            <select
              value={selectedDifficulty || ''}
              onChange={(e) => setSelectedDifficulty(e.target.value as AIDifficulty || undefined)}
            >
              <option value="">All Difficulties</option>
              <option value={AIDifficulty.EASY}>Easy</option>
              <option value={AIDifficulty.MEDIUM}>Medium</option>
              <option value={AIDifficulty.HARD}>Hard</option>
            </select>
          )}
        </div>

        <div className="leaderboard-list">
          {leaderboard.map((entry) => (
            <div key={entry.id} className="leaderboard-entry">
              <div className="rank">#{entry.rank}</div>
              <div className="player-info">
                {entry.avatar && <img src={entry.avatar} alt={entry.name} className="avatar" />}
                <span className="name">{entry.name}</span>
              </div>
              <div className="stats">
                <span className="wins">{entry.wins}W</span>
                <span className="total-games">{entry.totalGames}G</span>
                <span className="win-rate">{(entry.winRate * 100).toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAchievements = () => {
    if (!detailedStats) return null;

    return (
      <div className="achievements">
        <div className="achievements-grid">
          {detailedStats.achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`achievement ${achievement.unlocked ? 'unlocked' : 'locked'}`}
            >
              <div className="achievement-icon">{achievement.icon}</div>
              <div className="achievement-info">
                <h4>{achievement.name}</h4>
                <p>{achievement.description}</p>
                {achievement.unlocked && achievement.unlockedAt && (
                  <span className="unlock-date">
                    Unlocked: {new Date(achievement.unlockedAt).toLocaleDateString()}
                  </span>
                )}
                {!achievement.unlocked && achievement.progress !== undefined && achievement.maxProgress && (
                  <div className="progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${(achievement.progress / achievement.maxProgress) * 100}%` }}
                      />
                    </div>
                    <span className="progress-text">
                      {achievement.progress}/{achievement.maxProgress}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="statistics-display">
      <div className="statistics-header">
        <h2>Statistics</h2>
        <div className="view-tabs">
          <button
            className={viewMode === 'personal' ? 'active' : ''}
            onClick={() => setViewMode('personal')}
          >
            Personal
          </button>
          <button
            className={viewMode === 'leaderboard' ? 'active' : ''}
            onClick={() => setViewMode('leaderboard')}
          >
            Leaderboard
          </button>
          <button
            className={viewMode === 'achievements' ? 'active' : ''}
            onClick={() => setViewMode('achievements')}
          >
            Achievements
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="statistics-content">
          {viewMode === 'personal' && renderPersonalStatistics()}
          {viewMode === 'leaderboard' && renderLeaderboard()}
          {viewMode === 'achievements' && renderAchievements()}
        </div>
      )}
    </div>
  );
};